import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { TableConfig } from '@shared/interfaces/table/table-config';
import {
  DashboardQueryMissingMeteredDataDto,
  DashboardQueryMissingMeteredDataResultDto,
  DashboardService,
  MeteringPointSubType,
  MeteringPointType,
  MissingDataStatus,
  ObservationTimeSeriesPeriod,
  SettlementMethod,
} from '@app/api/dmc';
import { HeaderService } from '@app/core/services/header.service';
import { TableComponent } from '@shared/components/table/table.component';
import { CheckSquare, MessageSquare, Pencil, XSquare } from 'lucide-angular';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CardComponent } from '@shared/components/card/card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { missingMeteredDataTable } from '@shared/table-configs/dashboard/missing-metered-data.table';
import { Observable } from 'rxjs';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { ModalService } from '@app/core/services/modal.service';
import {
  EditTimeseriesModalComponent
} from '@shared/modals/timeseries/edit-timeseries-modal/edit-timeseries-modal.component';
import {
  MissingTimeseriesCommentModalComponent,
  CommentModalMode
} from '@shared/modals/timeseries/missing-timeseries-comment-modal/missing-timeseries-comment-modal.component';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { SortParam } from '@app/core/constants/sort-param';
import { ActivatedRoute, Router } from '@angular/router';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';

@Component({
  selector: 'app-dashboard-timeseries-missing',
  imports: [
    TableComponent,
    CardComponent,
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    InputDirective,
    ButtonDirective,
    SelectDirective,
    InputDateDirective,
    ToggleComponent,
  ],
  templateUrl: './dashboard-timeseries-missing.component.html',
  styleUrl: './dashboard-timeseries-missing.component.css'
})
export class DashboardTimeseriesMissingComponent implements AfterViewInit {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  public actions: MenuAction[] = [
    {
      id: 'edit',
      title: 'dashboard.timeseries.missing.edit',
      icon: Pencil,
      callback: () => this.openEditModal(),
      disabled: () => !(this.selectedElements.length === 1 && this.selectedElements[0].meterNumber),
    },
    {
      id: 'markHandled',
      title: 'dashboard.timeseries.missing.markHandled',
      icon: CheckSquare,
      callback: () => this.openCommentModal('markHandled'),
      disabled: () => this.selectedElements.length === 0 || this.toggleForm.get('showManuallyHandled')!.value,
    },
    {
      id: 'comment',
      title: 'dashboard.timeseries.missing.commentTitle',
      icon: MessageSquare,
      callback: () => this.openCommentModal('comment'),
      disabled: () => this.selectedElements.length === 0,
    },
    {
      id: 'unmarkHandled',
      title: 'dashboard.timeseries.missing.unmarkHandled',
      icon: XSquare,
      callback: () => this.unmarkAsHandled(),
      disabled: () => this.selectedElements.length === 0 || !this.toggleForm.get('showManuallyHandled')!.value,
    }
  ]

  /** Datepicker default date - Yesterday */
  public defaultDate: Date = new Date(new Date().setDate(new Date().getDate() - 1));

  /** Table **/
  public missingMeteredDataTableConfig: TableConfig<DashboardQueryMissingMeteredDataResultDto> = {
    ...missingMeteredDataTable,
    serviceCallback: (pageSize: number, page: number, sort?: SortParam) => this.search(pageSize, page, sort),
    contextActions: this.actions,
  }

  /** Data Carrier **/
  public tableData: DashboardQueryMissingMeteredDataDto[] = [];

  /** Selected Table Element */
  public selectedElements: DashboardQueryMissingMeteredDataResultDto[] = [];

  /** Toggle form for showing manually handled items */
  public toggleForm: FormGroup = new FormGroup({
    showManuallyHandled: new FormControl(false)
  });

  /** Search Form **/
  public searchForm: FormGroup = new FormGroup({
    dayOfWork: new FormControl(),
    streetName: new FormControl(''),
    buildingNumber: new FormControl(''),
    postalCode: new FormControl(''),
    estimationGroup: new FormControl(''),
    meteringPointId: new FormControl(''),
    missingDataStatus: new FormControl({ value: null, disabled: false }),
    meteringPointType: new FormControl({ value: null, disabled: false }),
    errorMessage: new FormControl(''),
    meteringPointSubType: new FormControl({ value: null, disabled: false }),
    settlementMethod: new FormControl({ value: null, disabled: false }),
    meterReadingOccurrence: new FormControl({ value: null, disabled: false }),
  })

  /** Dropdowns **/
  public meteringPointType: KeyValue[] = sortByTranslation(enumToKeyValue(MeteringPointType, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.meteringpoint.meteringpointtype.'
  }));
  public missingDataStatus: KeyValue[] = sortByTranslation(enumToKeyValue(MissingDataStatus, {
    omitKeys: ['NotSpecified', 'InfiniteCalculationLoop', 'EstimationCandidate', 'FailedAddMeteredData', 'CalculationResolutionMismatch', 'ReestimationCandidate'],
    prefixValue: 'enum.missingdatastatus.'
  }));
  public meteringPointSubType: KeyValue[] = enumToKeyValue(MeteringPointSubType, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.meteringpoint.meteringpointsubtype.'
  });
  public settlementMethod: KeyValue[] = enumToKeyValue(SettlementMethod, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.settlementmethod.'
  });
  public meterReadingOccurrence: KeyValue[] = enumToKeyValue(ObservationTimeSeriesPeriod, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'chargeInformation.observationPeriods.'
  });

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly headerService: HeaderService,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    this.headerService.setTitle('dashboard.timeseries.missing.title');
    this.headerService.setActions(this.actions);

    this.route.queryParams.subscribe(qp => {
      const showHandled = qp['showManuallyHandled'] === 'true';
      this.toggleForm.get('showManuallyHandled')!.setValue(showHandled, {
        emitEvent: false
      });
    });

    this.toggleForm.get('showManuallyHandled')!.valueChanges.subscribe(() => {
      this.table?.search();
    });
  }

  public ngAfterViewInit(): void {
    this.route.queryParams.subscribe((value) => {
      if (value) this.populateFormFromParams();
    });
  }

  /**
   * Check if a value is a "NotSpecified" enum value
   */
  private isNotSpecified(value: any): boolean {
    return value === MeteringPointSubType.NotSpecified ||
      value === SettlementMethod.NotSpecified ||
      value === ObservationTimeSeriesPeriod.NotSpecified ||
      value === MissingDataStatus.NotSpecified ||
      value === MeteringPointType.NotSpecified;
  }

  public search(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    const query = {
      ...this.searchForm.getRawValue(),
      dayOfWork: this.searchForm.get('dayOfWork')?.value === "" ? null : this.searchForm.get('dayOfWork')?.value,
      meteringPointSubType: this.searchForm.get('meteringPointSubType')?.value ?? MeteringPointSubType.NotSpecified,
      settlementMethod: this.searchForm.get('settlementMethod')?.value ?? SettlementMethod.NotSpecified,
      meterReadingOccurrence: this.searchForm.get('meterReadingOccurrence')?.value ?? ObservationTimeSeriesPeriod.NotSpecified,
      missingDataStatus: this.searchForm.get('missingDataStatus')?.value ?? MissingDataStatus.NotSpecified,
      meteringPointType: this.searchForm.get('meteringPointType')?.value ?? MeteringPointType.NotSpecified,
      showManuallyHandled: this.toggleForm.get('showManuallyHandled')!.value ?? false,
      countOnly: false,
    }

    return this.dashboardService.dashboardQueryMissingMeteredDataPost({ ...query, page, pageSize });
  }

  public openEditModal(): void {
    if (this.selectedElements.length !== 1) return;

    this.modalService.show({
      component: EditTimeseriesModalComponent,
      size: 'lg',
      title: 'dashboard.timeseries.missing.edit',
      actions: [
        { type: 'primary', label: 'system.button.edit', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        element: this.selectedElements[0],
      }
    })
  }

  public openCommentModal(mode: CommentModalMode): void {
    if (this.selectedElements.length === 0) return;

    this.modalService.show({
      component: MissingTimeseriesCommentModalComponent,
      size: 'base',
      title: mode === 'markHandled'
        ? 'dashboard.timeseries.missing.markHandled'
        : 'dashboard.timeseries.missing.commentTitle',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        elements: [...this.selectedElements],
        mode,
      }
    }).subscribe(() => {
      this.selectedElements = [];
      this.table?.search();
    });
  }

  public performSearch(): void {
    this.table?.search();
    this.updateUriWithParams();
  }

  public unmarkAsHandled(): void {
    if (this.selectedElements.length === 0) return;

    const items = this.selectedElements.map(el => ({
      meteringPointId: el.meteringPointId!,
      dayOfWork: el.dayOfWork,
      businessReasonCode: el.businessReasonCode!,
      dataFlag: el.missingDataStatus!,
      errorMessage: el.errorMessage ?? null,
    }));

    this.dashboardService.dashboardUnmarkMissingMeteredDataHandledPost({ items }).subscribe(() => {
      this.selectedElements = [];
      this.table?.search();
    });
  }

  /**
   * Updates the URL with current form values as query parameters
   * The NavigationService will automatically handle preserving these params in history
   */
  private updateUriWithParams(): void {
    if (!this.searchForm) return;

    const rawValues = this.searchForm.getRawValue();

    // Build query params object where empty/null/NotSpecified values are explicitly set to null
    // This ensures they are removed from the URL
    const queryParams: { [key: string]: string | null } = {};

    for (const [key, value] of Object.entries(rawValues)) {
      if (value === null || value === undefined || value === '' || this.isNotSpecified(value)) {
        // Explicitly set to null to remove from URL
        queryParams[key] = null;
      } else {
        // Keep the value
        queryParams[key] = value as string;
      }
    }

    // Include toggle state
    const showHandled = this.toggleForm.get('showManuallyHandled')!.value;
    queryParams['showManuallyHandled'] = showHandled ? 'true' : null;

    // Update URL with query params
    // Don't use 'merge' - we want to explicitly control all params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }

  /**
   * Populates the form from URL query parameters
   * This allows users to return to their previous search state
   */
  private populateFormFromParams(): void {
    if (!this.searchForm) return;

    const queryParams = this.route.snapshot.queryParams;

    if (Object.keys(queryParams).length > 0) {
      this.searchForm.patchValue(queryParams);
      this.searchForm.markAsDirty();

      // Trigger search with the restored params
      this.table?.search();
    }
  }

  /**
   * Resets the form and clears query parameters from URL
   */
  public resetForm(): void {
    this.searchForm.reset({
      dayOfWork: null,
      streetName: '',
      buildingNumber: '',
      postalCode: '',
      estimationGroup: '',
      meteringPointId: '',
      missingDataStatus: null,
      meteringPointType: null,
      errorMessage: '',
      meteringPointSubType: null,
      settlementMethod: null,
      meterReadingOccurrence: null,
    });

    // Clear query params from URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }
}
